import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const SDK = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const loadSdk = () => new Promise((ok, fail) => {
  if (window.AppleID) return ok();
  const s = document.createElement("script");
  s.src = SDK; s.onload = ok; s.onerror = fail;
  document.head.appendChild(s);
});

async function appleToken(cfg, purpose) {
  await loadSdk();
  const { nonce } = (await api.post("/auth/apple/nonce", { purpose })).data;
  window.AppleID.auth.init({ clientId: cfg.client_id, scope: "", redirectURI: cfg.redirect_uri, usePopup: true, nonce, state: crypto.randomUUID() });
  return (await window.AppleID.auth.signIn()).authorization.id_token;
}

function useAppleConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/auth/apple/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false })); }, []);
  return cfg;
}

const cls = "w-full h-12 rounded-lg bg-black text-white font-medium flex items-center justify-center gap-2 hover:bg-stone-800 transition-colors disabled:opacity-60";

// Sign-in only finds an Apple ID already linked to an 86'd account; it never creates or merges accounts by email.
export function AppleSignInButton({ onResult }) {
  const cfg = useAppleConfig();
  const [busy, setBusy] = useState(false);
  if (!cfg?.enabled) return null;
  const go = async () => {
    setBusy(true);
    try { onResult((await api.post("/auth/apple/signin", { id_token: await appleToken(cfg, "signin") })).data); }
    catch (e) { if (e?.error !== "popup_closed_by_user") toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return <button type="button" onClick={go} disabled={busy} className={`${cls} mt-3`} data-testid="apple-login-btn">{busy ? "Signing in…" : "Sign in with Apple"}</button>;
}

export function AppleLinkCard() {
  const cfg = useAppleConfig();
  const [st, setSt] = useState(null);
  const load = () => api.get("/auth/apple/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg?.enabled || !st) return null;
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { if (e?.error !== "popup_closed_by_user") toast.error(errMsg(e)); } };
  return (
    <div className="card-surface p-5 flex flex-wrap items-center gap-3" data-testid="apple-link-card">
      <div className="flex-1 min-w-0"><div className="font-semibold">Sign in with Apple</div>
        <p className="text-sm text-stone-600">{st.linked ? `Linked ${st.linked_at?.slice(0, 10)}. You can sign in to 86'd and the 86'd Inventory app with this Apple ID.` : "Link your Apple ID to this 86'd account so you can sign in with Apple."}</p></div>
      {st.linked
        ? <button onClick={() => act(() => api.post("/auth/apple/unlink"), "Apple unlinked")} className="h-9 px-4 rounded-full border text-sm" data-testid="apple-unlink-btn">Unlink</button>
        : <button onClick={() => act(async () => api.post("/auth/apple/link", { id_token: await appleToken(cfg, "link") }), "Apple linked")} className="h-9 px-4 rounded-full bg-black text-white text-sm" data-testid="apple-link-btn">Link Apple</button>}
    </div>
  );
}
